/**
 * Avisos de Gavi durante la prueba gratis sobre la categoría Rumbo (segunda
 * parte de «Rumbo también se planifica», decidido por Vadik el 1-nov-2026).
 *
 * - Día 30 (quedan 15), día 43 (quedan 2) y el día que acaba la prueba: Gavi
 *   dice cómo va Rumbo (apartado, falta algo o no hay categoría). Una hoja en
 *   Inicio y, con la app cerrada, un aviso al móvil en lugar de un toque.
 * - Sin categoría (la borró, eligió «Por mi cuenta» o hizo el tutorial antes
 *   de este paso): una sola vez, ofrece crearla.
 * - «Aún no tengo dinero» en el tutorial: cuando ya hay con qué, ofrece
 *   apartarlo.
 * - Tras el primer pago: «¿Apunto el cobro?», con el importe de verdad.
 *
 * El pago no es automático (sin tarjeta, por Google Play): Gavi habla de
 * «fin de la prueba» y «si sigues». Aquí solo hay funciones puras.
 */
import type {
  CobroPlan,
  EstadoPlan,
} from '@actual-app/core/server/rumbo/plan-app';
import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import type { TFunction } from 'i18next';

export type Hito = '30' | '43' | 'fin';

/** Días que quedan de prueba a partir de los que toca cada hito. */
export const QUEDAN_HITO = { '30': 15, '43': 2 } as const;

// ---------------------------------------------------------------------------
// Pref `rumbo-prueba`
// ---------------------------------------------------------------------------

/**
 * Lo que recuerda el presupuesto (pref sincronizada `rumbo-prueba`): los
 * avisos ya vistos y, si la categoría se creó desde Inicio (fuera del
 * tutorial), su id. La del tutorial vive en la pref `travesia`.
 */
export type PrefPrueba = {
  visto: string[];
  id?: string;
  pendiente?: boolean;
};

export function leerPrefPrueba(raw: string | null | undefined): PrefPrueba {
  try {
    const v: unknown = raw ? JSON.parse(raw) : null;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const o = v as Record<string, unknown>;
      return {
        visto: Array.isArray(o.visto)
          ? o.visto.filter((x): x is string => typeof x === 'string')
          : [],
        ...(typeof o.id === 'string' ? { id: o.id } : {}),
        ...(o.pendiente === true ? { pendiente: true } : {}),
      };
    }
  } catch {
    // Lo que no se entiende, como si no hubiera nada.
  }
  return { visto: [] };
}

/** Mezcla cambios y marca como vistas las claves de `vistas`. */
export function escribirPrefPrueba(
  previa: PrefPrueba,
  cambios: Partial<Omit<PrefPrueba, 'visto'>> & { vistas?: string[] },
): string {
  const { vistas = [], ...resto } = cambios;
  const siguiente: PrefPrueba = {
    ...previa,
    ...resto,
    visto: [...new Set([...previa.visto, ...vistas])].slice(-50),
  };
  if (!siguiente.pendiente) {
    delete siguiente.pendiente;
  }
  return JSON.stringify(siguiente);
}

/** Clave de un aviso de esta prueba (otra prueba o meses regalados lo rearman). */
export function claveHito(fin: string, hito: Hito | 'ingreso'): string {
  return `${fin}:${hito}`;
}
/** La oferta de crear la categoría se hace una sola vez, en cualquier prueba. */
export const CLAVE_CREAR = 'crear';
/** «¿Apunto el cobro?» tras el primer pago, una vez. */
export const CLAVE_PAGO = 'pago';

// ---------------------------------------------------------------------------
// Fechas
// ---------------------------------------------------------------------------

/** Día (local, `YYYY-MM-DD`) en que acaba la prueba. */
export function finPrueba(gratisHasta: number): string {
  return monthUtils.dayFromDate(new Date(gratisHasta * 1000));
}

/** Días de calendario que faltan hasta el fin de la prueba (0 = hoy). */
export function diasQuedan(fin: string, hoy: string): number {
  return monthUtils.differenceInCalendarDays(fin, hoy);
}

/** Hito que toca con `quedan` días de prueba, o null. */
export function hitoDe(quedan: number): Hito | null {
  if (quedan <= 0) {
    return 'fin';
  }
  if (quedan <= QUEDAN_HITO['43']) {
    return '43';
  }
  return quedan <= QUEDAN_HITO['30'] ? '30' : null;
}

// ---------------------------------------------------------------------------
// Cómo va la categoría
// ---------------------------------------------------------------------------

export type SituacionRumbo =
  | { tipo: 'sin' }
  | { tipo: 'falta'; apartado: IntegerAmount; falta: IntegerAmount }
  | { tipo: 'completo' };

/**
 * `saldo`: lo que tiene Rumbo en el mes del fin de la prueba (lo asignado
 * hasta ese mes, menos lo gastado). Sin categoría, `sin`.
 */
export function situacionRumbo(
  hayCategoria: boolean,
  saldo: IntegerAmount | null,
  precio: IntegerAmount,
): SituacionRumbo {
  if (!hayCategoria) {
    return { tipo: 'sin' };
  }
  const apartado = Math.max(0, saldo ?? 0);
  return apartado >= precio
    ? { tipo: 'completo' }
    : { tipo: 'falta', apartado, falta: precio - apartado };
}

// ---------------------------------------------------------------------------
// Qué hoja sale en Inicio
// ---------------------------------------------------------------------------

export type AvisoInicio =
  | { tipo: 'pago'; cobro: CobroPlan }
  | {
      tipo: 'hito';
      hito: Hito;
      quedan: number;
      /** Al cerrarla se dan por vistas (también las de hitos anteriores). */
      vistas: string[];
    }
  | { tipo: 'ingreso'; falta: IntegerAmount; vistas: string[] };

export type EntradaAviso = {
  plan: EstadoPlan | null;
  hoy: string;
  pref: PrefPrueba;
  situacion: SituacionRumbo;
  /** Quedó «Aún no tengo dinero» en el tutorial o en Inicio. */
  pendiente: boolean;
  /** «Listo para asignar» del mes en curso. */
  listo: IntegerAmount | null;
  /** El tutorial está en marcha (tapa Inicio). */
  tutorialEnMarcha: boolean;
};

/** La hoja de Gavi que toca en Inicio ahora, o null. */
export function avisoInicio(e: EntradaAviso): AvisoInicio | null {
  const { plan, pref, situacion } = e;
  if (!plan || e.tutorialEnMarcha) {
    return null;
  }
  const visto = new Set(pref.visto);
  if (
    plan.estado === 'activo' &&
    plan.cobro &&
    !plan.aBordo &&
    plan.tramo !== 'beta' &&
    situacion.tipo !== 'sin' &&
    !visto.has(CLAVE_PAGO)
  ) {
    return { tipo: 'pago', cobro: plan.cobro };
  }
  if (plan.estado !== 'prueba' || plan.gratisHasta == null || !plan.precios) {
    return null;
  }
  const fin = finPrueba(plan.gratisHasta);
  const quedan = diasQuedan(fin, e.hoy);
  const hito = hitoDe(quedan);
  if (hito) {
    const clave = claveHito(fin, hito);
    const anteriores = (['30', '43'] as const)
      .filter(h => h !== hito && (hito === 'fin' || h === '30'))
      .map(h => claveHito(fin, h));
    const sinCategoria = situacion.tipo === 'sin';
    // Sin categoría solo se ofrece crearla (día 30 o 43), y una vez.
    const toca = sinCategoria
      ? hito !== 'fin' && !visto.has(CLAVE_CREAR)
      : !visto.has(clave);
    if (toca) {
      return {
        tipo: 'hito',
        hito,
        quedan: Math.max(0, quedan),
        vistas: [clave, ...anteriores, ...(sinCategoria ? [CLAVE_CREAR] : [])],
      };
    }
  }
  if (
    e.pendiente &&
    situacion.tipo === 'falta' &&
    quedan > 0 &&
    (e.listo ?? 0) >= situacion.falta
  ) {
    const clave = claveHito(fin, 'ingreso');
    if (!visto.has(clave)) {
      return { tipo: 'ingreso', falta: situacion.falta, vistas: [clave] };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Textos (aprobados por Vadik el 1-nov-2026; una broma como mucho)
// ---------------------------------------------------------------------------

/** Importes y fecha ya formateados. */
export type FormatosPrueba = {
  /** Fin de la prueba, «15 de diciembre». */
  fecha: string;
  precio: string;
  apartado?: string;
  falta?: string;
};

export type TextoHito = { cuerpo: string; broma: string | null };

export function textoHito(
  t: TFunction,
  hito: Hito,
  quedan: number,
  situacion: SituacionRumbo,
  f: FormatosPrueba,
  /** Se apartó en el tutorial (hace 45 días), no después. */
  planificado: boolean,
): TextoHito {
  const count = quedan;
  if (situacion.tipo === 'sin') {
    return {
      cuerpo: t(
        '{{count}} days of your trial left. If you like, I will create the Rumbo category with its {{price}} for {{date}}. I am only asking once.',
        { count, price: f.precio, date: f.fecha },
      ),
      broma: null,
    };
  }
  if (hito === 'fin') {
    if (situacion.tipo === 'completo') {
      return {
        cuerpo: planificado
          ? t(
              'Your trial ends today. You planned it 45 days ago: Rumbo is already set aside. If you want to keep going, it is one tap; if not, your data stays here for 30 days so you can take it with you.',
            )
          : t(
              'Your trial ends today. Rumbo is already set aside. If you want to keep going, it is one tap; if not, your data stays here for 30 days so you can take it with you.',
            ),
        broma: t('For once, a date that arrives without a fright.'),
      };
    }
    return {
      cuerpo: t(
        'Your trial ends today. Rumbo has {{saved}} of {{price}}. You can cover it from another category in three taps, or not keep going: your data stays here for 30 days so you can take it with you.',
        { saved: f.apartado, price: f.precio },
      ),
      broma: null,
    };
  }
  if (situacion.tipo === 'completo') {
    return {
      cuerpo:
        hito === '30'
          ? t(
              '{{count}} days of your trial left. Rumbo already has its {{amount}}. Nothing to do.',
              { count, amount: f.precio },
            )
          : t('{{count}} days of your trial left. Rumbo is up to date.', {
              count,
            }),
      broma: null,
    };
  }
  return {
    cuerpo:
      hito === '30'
        ? t(
            '{{count}} days of your trial left. Rumbo is {{amount}} short. Shall we move it from another category?',
            { count, amount: f.falta },
          )
        : t('{{count}} days of your trial left. Rumbo is {{amount}} short.', {
            count,
            amount: f.falta,
          }),
    broma: null,
  };
}

/** Línea del aviso «ya hay con qué» (tras «Aún no tengo dinero»). */
export function textoIngreso(t: TFunction, fecha: string): string {
  return t('There is money now. One tap and Rumbo is set aside for {{date}}.', {
    date: fecha,
  });
}

/** Importe que sobra cada mes con descuento (céntimos), o null. */
export function sobraConDescuento(
  cobro: CobroPlan,
  precioMensual: IntegerAmount | null,
): IntegerAmount | null {
  if (cobro.periodo !== 'mensual' || precioMensual == null) {
    return null;
  }
  const sobra = precioMensual - cobro.importe;
  return sobra > 0 ? sobra : null;
}

/** «¿Apunto el cobro?» tras el primer pago. */
export function textoPago(
  t: TFunction,
  cobro: CobroPlan,
  f: { importe: string; sobra: string | null },
): { cuerpo: string; descuento: string | null } {
  return {
    cuerpo:
      cobro.periodo === 'anual'
        ? t(
            'First year paid. Thank you. Shall I note the charge in Rumbo? That way every charge arrives with the money ready.',
          )
        : t(
            'First month paid. Thank you. Shall I note the charge in Rumbo? That way every month arrives with the money ready.',
          ),
    descuento: f.sobra
      ? t('It came to {{amount}}; the extra {{rest}} stays in your category.', {
          amount: f.importe,
          rest: f.sobra,
        })
      : null,
  };
}

/**
 * Nota de la categoría tras pagar: el objetivo pasa a lo que se paga de
 * verdad, cada mes o cada año, desde el próximo cobro.
 */
export function lineasTrasPago(
  cobro: CobroPlan,
  proximo: string,
  importeDePlantilla: (importe: IntegerAmount) => string,
): string[] {
  const cada =
    cobro.periodo === 'anual' ? 'repeat every year' : 'repeat every 1 months';
  return [
    `#template ${importeDePlantilla(cobro.importe)} ${cada} starting ${proximo}`,
    `#objetivo día ${monthUtils.getDay(proximo)}`,
  ];
}

// ---------------------------------------------------------------------------
// Avisos al móvil con la app cerrada (service worker `public/avisos-sw.js`)
// ---------------------------------------------------------------------------

/** Un aviso de la prueba para un día concreto: el SW lo da en vez de un toque. */
export type AvisoPruebaMovil = {
  /** El SW da cada clave una sola vez. */
  clave: string;
  /** Día (`YYYY-MM-DD`) en que vale. */
  dia: string;
  texto: [string, string];
  ruta: string;
};

/**
 * Avisos de los días que quedan de prueba, con lo que se sabe ahora de
 * Rumbo. El tono «Tranquila» quita la broma y, sin cifras, no salen
 * importes en la pantalla bloqueada.
 */
export function avisosPruebaMovil(
  t: TFunction,
  {
    fin,
    hoy,
    situacion,
    formatos,
    planificado,
    tranquila,
    cifras,
    pref,
  }: {
    fin: string;
    hoy: string;
    situacion: SituacionRumbo;
    formatos: FormatosPrueba;
    planificado: boolean;
    tranquila: boolean;
    cifras: boolean;
    pref: PrefPrueba;
  },
): AvisoPruebaMovil[] {
  const visto = new Set(pref.visto);
  const res: AvisoPruebaMovil[] = [];
  const desde = Math.min(diasQuedan(fin, hoy), QUEDAN_HITO['30']);
  for (let quedan = desde; quedan >= 0; quedan--) {
    const hito = hitoDe(quedan);
    if (!hito) {
      continue;
    }
    const sinCategoria = situacion.tipo === 'sin';
    if (sinCategoria && (hito === 'fin' || visto.has(CLAVE_CREAR))) {
      continue;
    }
    const clave = sinCategoria ? CLAVE_CREAR : claveHito(fin, hito);
    if (visto.has(clave)) {
      continue;
    }
    let cuerpo: string;
    if (cifras) {
      const txt = textoHito(t, hito, quedan, situacion, formatos, planificado);
      cuerpo =
        txt.broma && !tranquila ? `${txt.cuerpo} ${txt.broma}` : txt.cuerpo;
    } else {
      cuerpo =
        hito === 'fin'
          ? t('Your trial ends today. Open Rumbo to see it.')
          : t('{{count}} days of your trial left. Open Rumbo to see it.', {
              count: quedan,
            });
    }
    res.push({
      clave,
      dia: monthUtils.subDays(fin, quedan),
      texto: ['Gavi', cuerpo],
      ruta: 'inicio',
    });
  }
  return res;
}
